<template>
  <base-modal
    ref="modal"
    :title="title"
    :loading="isSaving"
    @show="toggleModal"
    @hidden="resetModal"
    @ok="handleOk"
    ok-title="Save"
    cancel-title="Cancel"
    cancel-variant="light"
    footer-class="modal-footer-custom"
    header-class="modal-header-custom"
    body-class="modal-body-custom"
    content-class="modal-content-custom"
  >
    <form ref="form" @submit.stop.prevent="handleSubmit">
      <div class="mb-6 pb-6 border-b border-border">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-muted mb-4">Personal info</div>
        <div class="flex gap-4">
          <div class="mb-4 flex-1"><label for="firstName" class="block text-[13px] font-medium text-ink mb-1.5">First name</label>
            <input
              id="firstName"
              v-model="applicant.firstName"
              required
              placeholder="John"
              class="w-full border border-border-hover rounded-[10px] px-3.5 py-2.5 text-sm font-[inherit] leading-normal transition-[border-color] duration-150 focus:border-primary focus:ring-3 focus:ring-primary/10 outline-none"
            >
          </div>

          <div class="mb-4 flex-1"><label for="lastName" class="block text-[13px] font-medium text-ink mb-1.5">Last name</label>
            <input
              id="lastName"
              v-model="applicant.lastName"
              required
              placeholder="Doe"
              class="w-full border border-border-hover rounded-[10px] px-3.5 py-2.5 text-sm font-[inherit] leading-normal transition-[border-color] duration-150 focus:border-primary focus:ring-3 focus:ring-primary/10 outline-none"
            >
          </div>
        </div>

        <div class="mb-4"><label for="shortDescription" class="block text-[13px] font-medium text-ink mb-1.5">Short description</label>
          <input
            id="shortDescription"
            v-model="applicant.shortDescription"
            placeholder="e.g. Senior Engineer at Google"
            class="w-full border border-border-hover rounded-[10px] px-3.5 py-2.5 text-sm font-[inherit] leading-normal transition-[border-color] duration-150 focus:border-primary focus:ring-3 focus:ring-primary/10 outline-none"
          >
        </div>

        <div class="mb-4"><label for="description" class="block text-[13px] font-medium text-ink mb-1.5">Description</label>
          <textarea
            id="description"
            v-model="applicant.description"
            placeholder="Detailed background..."
            rows="3"
            max-rows="10"
            class="w-full border border-border-hover rounded-[10px] px-3.5 py-2.5 text-sm font-[inherit] leading-normal transition-[border-color] duration-150 focus:border-primary focus:ring-3 focus:ring-primary/10 outline-none">
          </textarea>
        </div>
      </div>

      <div class="mb-6 pb-6 border-b border-border">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-muted mb-4">Application</div>
        <div class="flex gap-4">
          <div class="mb-4 flex-1"><label for="type" class="block text-[13px] font-medium text-ink mb-1.5">Application type</label>
            <base-select v-model="applicant.type" :options="applicationTypes" value-key="value" label-key="text" placeholder="Select type"></base-select>
          </div>

          <div class="mb-4 flex-1"><label for="endorsingBody" class="block text-[13px] font-medium text-ink mb-1.5">Endorsing body</label>
            <base-select v-model="applicant.endorsingBody" :options="bodies" value-key="slug" label-key="title" placeholder="Select body"></base-select>
          </div>
        </div>

        <div class="mb-4"><label for="date" class="block text-[13px] font-medium text-ink mb-1.5">Deadline</label>
          <date-input
            v-model="applicant.deadline"
            placeholder="Select deadline"
            :required="true"
          ></date-input>
        </div>
      </div>

      <div class="mb-6 pb-6 border-b border-border">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-muted mb-4">Media</div>
        <div class="flex gap-4">
          <div class="mb-4 flex-1"><label for="picture" class="block text-[13px] font-medium text-ink mb-1.5">Picture filename</label>
            <input
              id="picture"
              v-model="applicant.picture"
              placeholder="photo.jpg"
              class="w-full border border-border-hover rounded-[10px] px-3.5 py-2.5 text-sm font-[inherit] leading-normal transition-[border-color] duration-150 focus:border-primary focus:ring-3 focus:ring-primary/10 outline-none"
            >
          </div>

          <div class="mb-4 flex-1"><label for="linkedin" class="block text-[13px] font-medium text-ink mb-1.5">LinkedIn URL</label>
            <input
              id="linkedin"
              v-model="applicant.linkedin"
              placeholder="https://linkedin.com/in/..."
              class="w-full border border-border-hover rounded-[10px] px-3.5 py-2.5 text-sm font-[inherit] leading-normal transition-[border-color] duration-150 focus:border-primary focus:ring-3 focus:ring-primary/10 outline-none"
            >
          </div>
        </div>
      </div>

      <div>
        <div class="text-[11px] font-semibold uppercase tracking-wide text-muted mb-4">Settings</div>
        <div class="flex gap-4">
          <div class="mb-4 flex-1"><label for="status" class="block text-[13px] font-medium text-ink mb-1.5">Status</label>
            <base-select v-model="applicant.isActive" :options="statusOptions" value-key="value" label-key="text" placeholder="Select status"></base-select>
          </div>

          <div class="mb-4 flex-1" v-if="user.isAdmin"><label for="payment" class="block text-[13px] font-medium text-ink mb-1.5">Payment</label>
            <base-select v-model="applicant.paid" :options="applicantPaymentOptions" value-key="value" label-key="text" placeholder="Select payment"></base-select>
          </div>
        </div>

        <div class="mb-4" v-if="user.isAdmin"><label class="block text-[13px] font-medium text-ink mb-1.5">Available to</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="opt in managers"
              :key="opt.value"
              type="button"
              class="flex items-center gap-2 px-2.5 py-1.5 rounded-full text-[13px] font-medium border cursor-pointer transition-all duration-150"
              :class="isAvailableTo(opt.value) ? 'bg-[#f0eef5] border-primary/30 text-primary' : 'bg-white border-border-hover text-muted hover:border-primary/20 hover:text-ink'"
              @click="toggleAvailableTo(opt.value)">
              <img v-if="getPersonPhoto(opt.value)" :src="getPersonPhoto(opt.value)" :alt="opt.text" class="w-5 h-5 rounded-full object-cover">
              <span>{{ opt.text }}</span>
              <svg v-if="isAvailableTo(opt.value)" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </button>
          </div>
        </div>
      </div>
    </form>
  </base-modal>
</template>

<script>
import { mapState } from 'vuex'
import dayjs from 'dayjs'
import DateInput from '../../UIComponents/DateInput'
import BaseSelect from '../../UIComponents/BaseSelect'
import { PHOTOS } from '@/services/photos'

export default {
  components: { DateInput, BaseSelect },
  props: {
    item: [Object]
  },
  data () {
    return {
      isSaving: false,
      applicant: {
        firstName: '',
        lastName: '',
        createdAt: '',
        isActive: true,
        paid: false,
        description: '',
        shortDescription: '',
        type: '',
        endorsingBody: '',
        deadline: '',
        availableTo: [],
        picture: '',
        linkedin: ''
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      statusOptions: state => state.auth.statusOptions,
      applicantPaymentOptions: state => state.applicants.applicantPaymentOptions,
      applicationTypes: state => state.auth.applicationTypes,
      bodies: state => state.managers.bodies,
      managers: state => state.managers.managers.filter(m => !m.hidden)
    }),
    title () {
      if (this.item) {
        return 'Edit applicant'
      } else {
        return 'Add applicant'
      }
    }
  },
  methods: {
    isAvailableTo (value) {
      return this.applicant.availableTo && this.applicant.availableTo.includes(value)
    },
    toggleAvailableTo (value) {
      const arr = this.applicant.availableTo || []
      const idx = arr.indexOf(value)
      if (idx >= 0) {
        arr.splice(idx, 1)
      } else {
        arr.push(value)
      }
      this.applicant.availableTo = [...arr]
    },
    getPersonPhoto (name) {
      return PHOTOS[name.toLowerCase()] || null
    },
    handleSubmit () {
      this.isSaving = true
      const { applicant } = this
      if (this.item) {
        // Track before/after changes
        const changes = []
        if ((this.item.deadline || '') !== (applicant.deadline || '')) {
          changes.push({ field: 'Deadline', from: this.item.deadline || 'none', to: applicant.deadline || 'none' })
        }
        if ((this.item.type || '') !== (applicant.type || '')) {
          changes.push({ field: 'Type', from: this.item.type || 'none', to: applicant.type || 'none' })
        }
        if ((this.item.endorsingBody || '') !== (applicant.endorsingBody || '')) {
          changes.push({ field: 'Endorsing body', from: this.item.endorsingBody || 'none', to: applicant.endorsingBody || 'none' })
        }
        if ((this.item.description || '') !== (applicant.description || '')) {
          changes.push({ field: 'Description', from: 'updated', to: 'changed' })
        }

        this.$store.dispatch('UPDATE_APPLICANT', applicant).then((response) => {
          this.isSaving = false
          this.$store.dispatch('SHOW_TOAST', { message: 'Applicant updated' })
          this.$emit('update')
          if (changes.length) {
            const userName = this.user ? (this.user.firstName || this.user.username) : 'Unknown'
            this.$store.dispatch('CREATE_ACTIVITY', {
              applicantId: applicant.id,
              type: 'edit',
              message: 'updated applicant details',
              user: userName,
              changes: changes
            }).catch(() => {})
          }
          this.$nextTick(() => {
            this.$refs.modal.hide()
          })
        }).catch(() => {
          this.isSaving = false
          this.$store.dispatch('SHOW_TOAST', { message: 'Failed to save applicant', type: 'error' })
        })
      } else {
        this.applicant.createdAt = dayjs().format()
        this.$store.dispatch('CREATE_APPLICANT', { applicant }).then((response) => {
          this.isSaving = false
          this.$store.dispatch('SHOW_TOAST', { message: 'Applicant added' })
          this.$emit('update')
          this.$nextTick(() => {
            this.$refs.modal.hide()
          })
        }).catch(() => {
          this.isSaving = false
          this.$store.dispatch('SHOW_TOAST', { message: 'Failed to add applicant', type: 'error' })
        })
      }
    },
    resetModal () {
      this.applicant.id = ''
      this.applicant.firstName = ''
      this.applicant.lastName = ''
      this.applicant.createdAt = ''
      this.applicant.isActive = true
      this.applicant.paid = false
      this.applicant.description = ''
      this.applicant.shortDescription = ''
      this.applicant.type = ''
      this.applicant.endorsingBody = ''
      this.applicant.deadline = ''
      this.applicant.availableTo = []
      this.applicant.picture = ''
      this.applicant.linkedin = ''
    },
    showModal () {
      this.$refs['modal'].show()
    },
    hideModal () {
      this.$refs['modal'].hide()
    },
    toggleModal () {
      this.resetModal()
      if (this.item) {
        this.applicant.id = this.item.id || ''
        this.applicant.firstName = this.item.firstName || ''
        this.applicant.lastName = this.item.lastName || ''
        this.applicant.createdAt = this.item.createdAt || ''
        this.applicant.isActive = this.item.isActive != null ? this.item.isActive : true
        this.applicant.paid = this.item.paid != null ? this.item.paid : false
        this.applicant.description = this.item.description || ''
        this.applicant.shortDescription = this.item.shortDescription || ''
        this.applicant.type = this.item.type || ''
        this.applicant.endorsingBody = this.item.endorsingBody || ''
        this.applicant.deadline = this.item.deadline || ''
        this.applicant.availableTo = this.item.availableTo || []
        this.applicant.picture = this.item.picture || ''
        this.applicant.linkedin = this.item.linkedin || ''
      }
    },
    handleOk (bvModalEvt) {
      bvModalEvt.preventDefault()
      this.handleSubmit()
    }
  },
  mounted () {
  },
  created () {
  }
}
</script>


<style>
.modal-content-custom {
  border: none !important;
  border-radius: 16px !important;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.modal-header-custom {
  padding: 28px 28px 0 !important;
  border: none !important;
}

.modal-header-custom .modal-title {
  font-size: 20px !important;
  font-weight: 700 !important;
  color: #131415 !important;
}

.modal-body-custom {
  padding: 24px 28px !important;
}

.modal-footer-custom {
  padding: 0 28px 28px !important;
  border: none !important;
  display: flex !important;
  justify-content: flex-end !important;
  gap: 10px !important;
}

.modal-footer-custom .btn-secondary,
.modal-footer-custom .btn-light {
  background: #f4f5fa !important;
  border: none !important;
  color: #131415 !important;
  border-radius: 10px !important;
  padding: 10px 24px !important;
  font-weight: 500 !important;
}

.modal-footer-custom .btn-primary {
  background: #131415 !important;
  border: none !important;
  color: #fff !important;
  border-radius: 10px !important;
  padding: 10px 24px !important;
  font-weight: 500 !important;
}

.modal-footer-custom .btn-primary:hover {
  opacity: 0.85;
}

.modal-footer-custom .btn-danger {
  background: #e53935 !important;
  border: none !important;
  color: #fff !important;
  border-radius: 10px !important;
  padding: 10px 24px !important;
  font-weight: 500 !important;
}

.modal-footer-custom .btn-danger:hover {
  opacity: 0.85;
}

.modal-content-custom .form-control,
.modal-content-custom .custom-select {
  border: 1px solid #d6d8e3 !important;
  border-radius: 10px !important;
  padding: 10px 14px !important;
  font-size: 14px !important;
  transition: border-color 0.15s !important;
}

.modal-content-custom .form-control:focus,
.modal-content-custom .custom-select:focus {
  border-color: #7c5bea !important;
  box-shadow: 0 0 0 3px rgba(124, 91, 234, 0.1) !important;
}

.modal-content-custom .form-group label {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #131415 !important;
  margin-bottom: 6px !important;
}
</style>
